/**
 * Report List Hook
 * Business logic for report list with pagination, sorting, filtering
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { useConfirmation } from '@/contexts/confirmation'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getCustomerById } from '@/modules/Customer/services'
import {
    listReportWaf,
    deleteReportWaf,
    toggleReportWafStatus,
    resendReport,
    viewReport,
} from '../services'
import type { ReportWAF } from '../types/response'
import type { ResendType } from '../types/payload'
import {
    getReportColumns,
    getReportActions,
    getReportFilterItems,
    REPORT_SORT_CONFIG,
    REPORT_FILTER_DEFAULTS,
} from '../utils/report-list-config'

interface ReportFilters {
    send_frequency: string
    send_format: string
    status: string
    priority_min: string
    priority_max: string
    registration_date: string[]
}

export const useReportList = () => {
    const { t } = useTranslation('report_waf')
    const navigate = useNavigate()
    const { setConfirmation } = useConfirmation()

    // Dialogs
    const [resendDialog, setResendDialog] = useState<[boolean, string]>([
        false,
        '',
    ])
    const [viewDialog, setViewDialog] = useState<[boolean, string]>([false, ''])

    // URL params
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<ReportFilters>({
        defaults: {
            page: 1,
            page_size: 10,
            ...REPORT_SORT_CONFIG.defaultSort,
        },
        filterDefaults: REPORT_FILTER_DEFAULTS,
    })

    const { control } = useForm<ReportFilters>({
        defaultValues: filters,
    })

    // Fetch
    const { data, renderState, isFetching } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.REPORT_WAF, queryParams],
        queryFn: () =>
            listReportWaf({
                page: params.page,
                page_size: params.page_size,
                q: queryParams.q,
                filters: queryParams.filters,
                sort_by: params.sort_by,
                sort_order: params.sort_order,
            }),
        placeholderData: true,
    })

    const rawReports = data?.results || []
    const pagination = data?.pagination

    // Resolve customer names from datacentral
    const [customerMap, setCustomerMap] = useState<Record<number, string>>({})

    useEffect(() => {
        if (!rawReports.length) return

        const uniqueIds = [
            ...new Set(
                rawReports
                    .map((r) => r.customer_id)
                    .filter((id) => id > 0 && !customerMap[id])
            ),
        ]
        if (!uniqueIds.length) return

        Promise.allSettled(
            uniqueIds.map((id) =>
                getCustomerById(id).then((res) => ({
                    id,
                    name:
                        res?.result?.company?.company_name ||
                        res?.result?.customer_name ||
                        '',
                }))
            )
        ).then((results) => {
            const newMap: Record<number, string> = {}
            results.forEach((r) => {
                if (r.status === 'fulfilled' && r.value.name) {
                    newMap[r.value.id] = r.value.name
                }
            })
            if (Object.keys(newMap).length) {
                setCustomerMap((prev) => ({ ...prev, ...newMap }))
            }
        })
    }, [rawReports])

    // Merge customer names into reports
    const reports = useMemo(
        () =>
            rawReports.map((r) => ({
                ...r,
                customer_name: customerMap[r.customer_id] || r.customer_name,
            })),
        [rawReports, customerMap]
    )

    // Mutations
    const { mutateAsync: doDelete } = useMutationWithHandler(
        { mutationFn: (id: string) => deleteReportWaf(id) },
        {
            reactQueryKeys: QUERY_KEYS.REPORT_WAF,
            successMessage: t('delete-success'),
            errorMessage: t('delete-error'),
        }
    )

    const { mutateAsync: doToggleStatus } = useMutationWithHandler(
        {
            mutationFn: (p: {
                id: string
                status: 'activate' | 'deactivate'
            }) => toggleReportWafStatus(p.id, p.status),
        },
        {
            reactQueryKeys: QUERY_KEYS.REPORT_WAF,
            successMessage: t('status_toggle_success'),
            errorMessage: t('status_toggle_error'),
        }
    )

    const { mutateAsync: doResend, isPending: isResending } =
        useMutationWithHandler(
            { mutationFn: (payload: ResendType) => resendReport(payload) },
            {
                reactQueryKeys: QUERY_KEYS.REPORT_WAF,
                successMessage: t('notifications.resend_success'),
                errorMessage: t('errors.resend_failed'),
                onSuccess: () => setResendDialog([false, '']),
            }
        )

    const { mutateAsync: doView, isPending: isViewing } =
        useMutationWithHandler(
            { mutationFn: viewReport },
            {
                reactQueryKeys: QUERY_KEYS.REPORT_WAF,
                successMessage: t('view_dialog.success'),
                errorMessage: t('errors.view_failed'),
                onSuccess: (data) => {
                    setViewDialog([false, ''])
                    if (data?.preview_url) {
                        window.open(data.preview_url, '_blank')
                    }
                },
            }
        )

    // Columns & filters
    const columns = useMemo(() => getReportColumns(t), [t])
    const filterItems = useMemo(() => getReportFilterItems(t), [t])

    // Action handlers
    const handleResend = useCallback(
        (row: ReportWAF) => setResendDialog([true, row.id]),
        []
    )
    const handleToggleStatus = useCallback(
        (row: ReportWAF) => {
            const action =
                row.status === 'activate'
                    ? t('dialogs.deactivate_action')
                    : t('dialogs.activate_action')
            setConfirmation({
                open: true,
                title: t('dialogs.toggle_status_title'),
                description: t('dialogs.toggle_status_message', {
                    action,
                    customer: row.customer_name,
                }),
                onConfirm: () =>
                    doToggleStatus({
                        id: row.id,
                        status:
                            row.status === 'activate'
                                ? 'deactivate'
                                : 'activate',
                    }),
            })
        },
        [setConfirmation, doToggleStatus, t]
    )
    const handleViewHistory = useCallback(
        (row: ReportWAF) => navigate(`/technical/report/${row.id}/history`),
        [navigate]
    )
    const handleView = useCallback(
        (row: ReportWAF) => setViewDialog([true, row.id]),
        []
    )
    const handleEdit = useCallback(
        (row: ReportWAF) => navigate(`/technical/report/${row.id}/edit`),
        [navigate]
    )
    const handleDelete = useCallback(
        (row: ReportWAF) =>
            setConfirmation({
                open: true,
                title: t('dialogs.delete_title'),
                description: t('dialogs.delete_message_detail', {
                    customer: row.customer_name,
                }),
                onConfirm: () => doDelete(row.id),
            }),
        [setConfirmation, doDelete, t]
    )

    const actions = useMemo(
        () =>
            getReportActions(t, {
                onResend: handleResend,
                onToggleStatus: handleToggleStatus,
                onViewHistory: handleViewHistory,
                onView: handleView,
                onEdit: handleEdit,
                onDelete: handleDelete,
            }),
        [
            t,
            handleResend,
            handleToggleStatus,
            handleViewHistory,
            handleView,
            handleEdit,
            handleDelete,
        ]
    )

    // Pagination & sorting
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (size: number) => setParams({ page_size: size }),
        [setParams]
    )
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    return {
        // Data
        reports,
        pagination,
        stateComponent: renderState(),
        isFetching,

        // Params & filters
        params,
        control,
        setFilter,
        searchInputProps,
        filterItems,

        // Columns & actions
        columns,
        actions,

        // Handlers
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,

        // Dialogs
        resendDialog,
        setResendDialog,
        viewDialog,
        setViewDialog,
        doResend,
        isResending,
        doView,
        isViewing,

        // Translation
        t,
    }
}
